import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, InputGroup, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FaCopy } from 'react-icons/fa';

const PaymentPage = () => {
  const [selectedCrypto, setSelectedCrypto] = useState('btc');
  const [prices, setPrices] = useState(null);
  const [dots, setDots] = useState('');
  const [usdAmount] = useState(parseFloat(atob(new URLSearchParams(window.location.search).get('a') || btoa(10)))); 
  const navigate = useNavigate();
  
  const wallets = {
    "btc":"bc1qvpp64apaj2l00n6md4um2x9k44u8uhkpslk05v",
    "ltc":"Li2fEVD1XNWQwgjGS6gCvDdwDiPwWcVuza",
    "doge":"DQ8rfRKqvsw2K23MyF427A3TC8b5sWtk7D",
    "xmr":"43sRpX6pjsf6o7gVh4RfnH9hvPv7zk96cB411fhgAoQFH7E6RWnGAjhQDR3AddSkse6gRb1Le82F1aMQeALrnTCpQ7tSXN5",
    "trx":"TYvd3aiF622PDBRseKgfBkzc15J9vymbMK",
    "sol":"8u5xBKe7Mini5tmtX5uZz6WJLJpqTmQtH4gz3JwERqsV",
    "usdt-erc":"0x194e124bDB282b1C2d0943e758d4744C236C7513",
    "usdt-trx":"TYvd3aiF622PDBRseKgfBkzc15J9vymbMK"
}

  const longnames = {
    "btc": "bitcoin",
    "ltc": "litecoin",
    "doge": "dogecoin",
    "xmr": "monero",
    "sol": "solana",
    "trx": "tron",
    "usdt-erc": "tether",
    "usdt-trx": "tether"
  };

  const cryptoOptions = [
    { value: 'btc', label: 'Bitcoin (BTC)' },
    { value: 'ltc', label: 'Litecoin (LTC)' },
    { value: 'doge', label: 'Dogecoin (DOGE)' },
    { value: 'xmr', label: 'Monero (XMR)' },
    { value: 'sol', label: 'Solana (SOL)' },
    { value: 'trx', label: 'Tron (TRX)' },
    { value: 'usdt-erc', label: 'Tether (USDT) (ETH ERC-20)' },
    { value: 'usdt-trx', label: 'Tether (USDT) (TRX TRC-20)' }
  ];

  const instructions = [
    "1. Choose your preferred cryptocurrency from the dropdown menu",
    "2. Copy the exact amount shown in the 'Amount to send' field",
    "3. Copy the wallet address",
    "4. Open your crypto wallet or exchange",
    "5. Create a new transaction using the copied address",
    "6. Paste the exact amount copied in step 2",
    "7. Double check the address and amount before sending",
    "8. Complete the transaction",
    "9. Wait for confirmation (this may take a few minutes)",
    "10. Once confirmed, the 'Continue' button will be enabled"
  ];

  useEffect(() => {
    fetch("https://api.coingecko.com/api/v3/simple/price?ids=bitcoin,litecoin,dogecoin,monero,tron,solana,tether&vs_currencies=usd")
      .then(res => res.json())
      .then(data => setPrices(data));

    const interval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 500);

    return () => clearInterval(interval);
  }, []);

  const copyToClipboard = (text, inputId) => {
    try {
      // Try modern clipboard API first
      navigator.clipboard.writeText(text);
    } catch (err) {
      // Fallback to older method
      const inputField = document.getElementById(inputId);
      if (inputField) {
        inputField.select();
        inputField.setSelectionRange(0, 99999);
        try {
          document.execCommand('copy');
        } catch (err) {
          console.error('Failed to copy text:', err);
        }
      }
    }
  };

  const getAmount = () => {
    if (!prices) return '0.00';
    return (usdAmount / prices[longnames[selectedCrypto]]?.usd).toFixed(8);
  };

  return (
    <Container className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <Button variant="outline-primary" onClick={() => navigate('/')}>
          ← Home
        </Button>
        <h1 className="m-0">Pay with Crypto</h1>
      </div>

      <Card className="bg-dark text-light">
        <Card.Body>
          <Row>
            <Col xs={12} sm={12} md={6} className="mb-4 mb-md-0">
              <Form>
                <Form.Group className="mb-4">
                  <Form.Label>Choose payment method:</Form.Label>
                  <Form.Select 
                    className="bg-dark text-light border-secondary"
                    value={selectedCrypto}
                    onChange={(e) => setSelectedCrypto(e.target.value)}
                  >
                    {cryptoOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>USD Amount:</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      value={`$${usdAmount.toFixed(2)}`}
                      readOnly
                      className="bg-dark text-light border-secondary"
                    />
                  </InputGroup>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>Amount to send:</Form.Label>
                  <InputGroup>
                    <Form.Control
                      id="crypto-amount"
                      type="text"
                      value={getAmount()}
                      readOnly
                      className="bg-dark text-light border-secondary"
                    />
                    <Button 
                      variant="outline-secondary"
                      onClick={() => copyToClipboard(getAmount(), 'crypto-amount')}
                    >
                      <FaCopy />
                    </Button>
                  </InputGroup>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>{`${selectedCrypto.toUpperCase()} Address:`}</Form.Label>
                  <InputGroup>
                    <Form.Control
                      id="wallet-address"
                      type="text"
                      value={wallets[selectedCrypto]}
                      readOnly
                      className="bg-dark text-light border-secondary"
                    />
                    <Button 
                      variant="outline-secondary"
                      onClick={() => copyToClipboard(wallets[selectedCrypto], 'wallet-address')}
                    >
                      <FaCopy />
                    </Button>
                  </InputGroup>
                </Form.Group>

                <div className="text-center">
                  <p className="text-muted">
                    Status: Waiting for payment{dots}
                  </p>
                  
                  <Button variant="primary" className="w-100 mb-3" disabled>
                    Continue
                  </Button>

                  <div className="text-muted small">
                    Note: If you completed the payment but status hasn't updated, please{' '}
                    <Button variant="link" className="p-0" onClick={() => navigate('/contact_us')}>
                      contact us
                    </Button>
                  </div>
                </div>
              </Form>
            </Col>

            <Col xs={12} sm={12} md={6}>
              <div className="h-100 d-flex flex-column justify-content-center">
                <div className="border-start border-secondary ps-4 h-100 d-none d-md-block">
                  <h4 className="mb-4">Payment Instructions</h4>
                  <ul className="list-unstyled">
                    {instructions.map((instruction, index) => (
                      <li key={index} className="mb-3 text-muted">
                        {instruction}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="d-md-none mt-4 pt-4 border-top border-secondary">
                  <h4 className="mb-4">Payment Instructions</h4>
                  <ul className="list-unstyled">
                    {instructions.map((instruction, index) => (
                      <li key={index} className="mb-3 text-muted">
                        {instruction}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default PaymentPage; 